import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { TargetedEvent } from '@core/helpers/events/targeted-event';
import { SortHelper } from '@core/helpers/sort.helper';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { ImportedProjectStructureDto } from '@interfaces/ImportedProjectStructureDto';
import { Level1 } from '@interfaces/Level1';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { StructureService } from '@shared/services/structure.service';
import { TreeNode } from 'primeng/api';
import { TreeNodeType } from 'src/app/estimate/treetable/models/tree-node-data.model';

@Component({
  selector: 'pop-import-modal',
  templateUrl: 'import-modal.component.html',
  styleUrls: ['import-modal.component.less']
})
export class ImportModalComponent extends BaseComponent implements OnInit {
  public currentEstimate: Estimate;
  public importTree: TreeNode<ImportedProjectStructureDto>[];
  private projectTemplateStructures: ImportedProjectStructureDto[];
  public selectedNodes: TreeNode<ImportedProjectStructureDto>[] = [];
  public settings: EstimateConfiguration;

  public constructor(
    private estimateService: EstimateService,
    private estimateSettings: EstimateSettingsService,
    private structureService: StructureService,
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    super();
  }

  public async ngOnInit() {
    await this.loadingService.run(async () => {
      this.currentEstimate = this.estimateService.currentEstimate;

      this.settings = await this.estimateSettings.getEstimateConfigurationAsync(false);

      this.projectTemplateStructures = await this.estimateService.getProjectStructuresAsync(
        this.currentEstimate.projectModelCode
      );

      this.createImportTree();
    });
  }

  private createImportTree() {
    const structures = SortHelper.sortArrayByProperty(this.projectTemplateStructures, o => o.level1Code + o.level2Code);

    const importNode = this.createNode(this.currentEstimate.projectModelCode, '', undefined, TreeNodeType.Project);

    this.importTree = [importNode];

    for (const structure of structures) {
      if (structure.level1Code) {
        let level1Node = importNode.children.find(c => c.data?.level1Code === structure.level1Code);

        if (!level1Node) {
          level1Node = this.createNode(
            structure.level1Code,
            structure.level1Desc,
            structure,
            TreeNodeType.Level1,
            importNode
          );

          importNode.children.push(level1Node);
        }

        //add level2s to Level1
        if (structure.level2Code) {
          const level2Node = this.createNode(
            structure.level2Code,
            structure.level2Desc,
            structure,
            TreeNodeType.Level2,
            level1Node
          );

          level1Node.children.push(level2Node);
        }
      }
    }
  }

  public saveChanges() {
    this.estimateService.saveChanges().subscribe();
    this.estimateService.dialogEmitChanges();
  }

  public onSelect(e: Event, node: TreeNode<ImportedProjectStructureDto>) {
    const targetedEvent = e as TargetedEvent<HTMLInputElement>;
    const isChecked = targetedEvent.target.checked;

    if (isChecked) {
      this.selectedNodes.push(node);
    } else {
      const i = this.selectedNodes.indexOf(node);
      if (i > -1) {
        this.selectedNodes.splice(i, 1);
      }
    }
  }

  public async closeModal() {
    await this.router.navigate(['..'], {
      relativeTo: this.activatedRoute
    });
  }

  private createNode(
    code: string,
    description: string,
    data: ImportedProjectStructureDto,
    type: TreeNodeType,
    parent?: TreeNode<ImportedProjectStructureDto>
  ): TreeNode<ImportedProjectStructureDto> {
    code = code ? code : '---';
    description = description ? description : '---';

    const treeNode: TreeNode<ImportedProjectStructureDto> = {
      data: data,
      label: code + ' \u2022 ' + description,
      parent: parent,
      type: type,
      expanded: true,
      children: [],
      key: `${code}_${parent?.key}`
    };

    return treeNode;
  }

  public async importTemplates() {
    await this.loadingService.run(async () => {
      const createdLevel1: Level1[] = [];

      const level1sToCreate = this.selectedNodes.filter(n => n.type === TreeNodeType.Level1);
      const level2sToCreate = this.selectedNodes.filter(n => n.type === TreeNodeType.Level2);

      for (const level1Node of level1sToCreate) {
        const level1IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod(level1Node.data.level1IsLumpSum);
        const level2IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod(level1Node.data.level2IsLumpSum);

        const level1 = this.structureService.createLevel1(level1Node.data);
        createdLevel1.push(level1);

        const hasSelectedLevel2 = level2sToCreate.find(l => l.data.level1Code === level1Node.data.level1Code);

        if (hasSelectedLevel2) {
          continue; // dont add structure yet
        }
        this.estimateService.addStructure(
          level1.id,
          null,
          level1Node.data.categoryGroupId,
          level1Node.data,
          level1IsUsingLaborBillingMethod,
          level2IsUsingLaborBillingMethod
        );
      }

      for (const node of level2sToCreate) {
        const level1IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod(node.data.level1IsLumpSum);
        const level2IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod(node.data.level2IsLumpSum);

        let level1 = createdLevel1.find(l => l.level1ModelCode === node.data.level1Code);

        if (!level1) {
          level1 = this.currentEstimate.structures.find(l => l.level1.code === node.data.level1Code)?.level1;
        }

        if (!level1) {
          level1 = this.structureService.createLevel1(node.parent.data);
          createdLevel1.push(level1);
        }

        const level2 = this.structureService.createLevel2(node.data, level1);

        this.estimateService.addStructure(
          level1.id,
          level2.id,
          node.data.categoryGroupId,
          node.data,
          level1IsUsingLaborBillingMethod,
          level2IsUsingLaborBillingMethod
        );
      }

      await this.estimateService.saveChangesAsync();
      await this.estimateService.refreshEstimateAsync();
      await this.closeModal();
    });
  }

  /**
   * Used to determine the value of a new sublevel's UseLaborBillingMethod property
   * Rules :
   * If the company forces their billing method setting, return the company setting value;
   * Else
   *  If the company does not allow applying a billing method to a lump sum sublevel and the sublevel is lump sum, return false;
   *  Else return the estimate's default value
   * @param sublevelIsLumpSum
   */
  private getSublevelUseLaborBillingMethod(sublevelIsLumpSum = false) {
    if (this.settings.forceLaborBillingMethod) {
      return this.settings.useLaborBillingMethod;
    } else {
      if (!this.settings.useLaborBillingMethodWhenLumpSum && sublevelIsLumpSum) {
        return false;
      }

      return this.currentEstimate.useLaborBillingMethod;
    }
  }
}
